import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import ComicCard from '@/components/base/ComicCard';
import { comics } from '@/mocks/comics';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);

  if (!comic) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
            </div>
            <h2 className="font-heading text-xl text-foreground-900 mb-2">
              漫画未找到
            </h2>
            <Link
              to="/"
              className="text-primary-500 hover:text-primary-600 text-sm"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === comic.category);
  const viewsText = comic.views >= 10000 ? `${(comic.views / 10000).toFixed(1)}万` : comic.views.toString();
  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 4);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画免费看`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: comic.chapters[0]?.date,
    dateModified: comic.updateDate,
    image: comic.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '韩漫在线阅读网',
    },
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-20 pb-14 md:pb-20 bg-background-50">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
              { label: comic.title },
            ]}
          />

          <script type="application/ld+json">
            {JSON.stringify(articleSchema)}
          </script>

          <article className="flex flex-col lg:flex-row gap-8 mt-4">
            <div className="lg:w-[320px] shrink-0">
              <div className="sticky top-24">
                <div className="rounded-lg overflow-hidden aspect-[3/4]">
                  <img
                    src={comic.coverImage}
                    alt={`${comic.title} - 韩国漫画免费看`}
                    title={`${comic.title} - 韩漫在线阅读`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                {comic.featured && (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-accent-500 text-background-50">
                    精选
                  </span>
                )}
                {comic.isNew && (
                  <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-500 text-background-50">
                    新作
                  </span>
                )}
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  comic.status === '连载中' ? 'bg-primary-100 text-primary-700' : 'bg-secondary-100 text-secondary-700'
                }`}>
                  {comic.status}
                </span>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">
                {comic.title}
              </h1>

              <div className="flex items-center gap-4 mb-5 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-star-fill text-primary-500 text-sm"></i>
                  </div>
                  <span className="font-bold text-foreground-900">{comic.rating}</span>
                  <span className="text-foreground-400 text-sm">/ 10</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-eye-line"></i>
                  </div>
                  <span>{viewsText} 次阅读</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-user-line"></i>
                  </div>
                  <span>作者: {comic.author}</span>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-foreground-500">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-paint-brush-line"></i>
                  </div>
                  <span>画师: {comic.artist}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-5 flex-wrap">
                {comic.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 rounded-full text-xs bg-secondary-100 text-secondary-700">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-100 rounded-lg p-5 mb-6">
                <h2 className="font-heading text-lg text-foreground-900 mb-3">
                  作品简介
                </h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {comic.description}
                </p>
              </div>

              <div>
                <h2 className="font-heading text-lg text-foreground-900 mb-4">
                  章节列表
                  <span className="text-sm text-foreground-400 font-normal ml-2">
                    共 {comic.chapters.length} 话
                  </span>
                </h2>
                <div className="space-y-1 max-h-[420px] overflow-y-auto pr-2">
                  {comic.chapters.map((chapter) => (
                    <button
                      key={chapter.id}
                      className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-background-100 hover:bg-background-200 transition-colors text-left cursor-pointer"
                    >
                      <span className="text-sm text-foreground-800 font-medium">
                        {chapter.title}
                      </span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap ml-4">
                        {chapter.date}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </article>

          {relatedComics.length > 0 && (
            <section className="mt-14 md:mt-20">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-6">
                相关推荐
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}